import { Injectable, inject, signal } from '@angular/core';
import Keycloak, { KeycloakLoginOptions } from 'keycloak-js';
import { environment } from '../../environments/environment';
import { Product } from '../types/product.types';
import { I18nService } from './i18n.service';

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private readonly i18n = inject(I18nService);
  private readonly _products = signal<Record<string, Product>>(
    environment.products as Record<string, Product>,
  );
  private readonly _keycloakInstances = new Map<string, Promise<Keycloak>>();

  readonly products = this._products.asReadonly();

  getProduct(key: string): Product | undefined {
    return this._products()[key];
  }

  login(key: string, redirectPath?: string): Promise<void> {
    return this._keycloakAction(key, (kc, opts) => kc.login(opts), redirectPath);
  }

  register(key: string, redirectPath?: string): Promise<void> {
    return this._keycloakAction(key, (kc, opts) => kc.register(opts), redirectPath);
  }

  private async _keycloakAction(
    key: string,
    action: (kc: Keycloak, options: KeycloakLoginOptions) => Promise<void>,
    redirectPath?: string,
  ): Promise<void> {
    const product = this.getProduct(key);
    if (!product?.authenticationConfig) {
      throw new Error(`No authentication config for product: ${key}`);
    }
    const kc = await this._getOrCreateKeycloak(key, product.authenticationConfig);
    const options: KeycloakLoginOptions = { locale: this.i18n.locale() };
    const path = redirectPath ?? product.authenticationConfig.redirect;
    options.redirectUri = `${product.url}/${path}`;
    return action(kc, options);
  }

  private _getOrCreateKeycloak(
    key: string,
    { url, realm, clientId }: NonNullable<Product['authenticationConfig']>,
  ): Promise<Keycloak> {
    const existing = this._keycloakInstances.get(key);
    if (existing) return existing;
    const kc = new Keycloak({ url, realm, clientId });
    const promise = kc.init({ checkLoginIframe: false }).then(() => kc);
    this._keycloakInstances.set(key, promise);
    return promise;
  }
}
